import { useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft, Pencil, Key, Upload, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import SettingsLayout from '@/layouts/SettingsLayout'
import { useOrgPath } from '@/lib/pathFor'
import { formatBookingRef } from '@/lib/formatters'
import { shortContainerType } from '@/lib/constants'

interface CarrierDetail {
  id: string
  name: string
  code: string | null
  contact_email: string | null
  contact_phone: string | null
  website: string | null
}

interface BookingRow {
  id: string
  carrier_booking_id: string
  client_email: string | null
  origin_port: string | null
  destination_port: string | null
  sailing_date: string | null
  container_count: number | null
  container_type: string | null
  show_url: string
}

interface RateSheetRow {
  id: string
  original_filename: string
  parse_status: string
  parse_format: string | null
  row_count: number
  effective_date: string | null
  expiry_date: string | null
  created_at: string
  show_url: string
}

interface CarriersShowProps {
  carrier: CarrierDetail
  bookings: BookingRow[]
  rate_sheets: RateSheetRow[]
}

export default function CarriersShow({ carrier, bookings, rate_sheets }: CarriersShowProps) {
  const pathFor = useOrgPath()
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  // Upload a rate sheet pre-assigned to this carrier — the existing rate-sheet
  // create endpoint reads carrier_id and lands on the parsed sheet.
  function handleRateSheetFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('carrier_id', carrier.id)
    setUploading(true)
    router.post(pathFor('/settings/rate_sheets'), formData, {
      onFinish: () => {
        setUploading(false)
        if (fileInput.current) fileInput.current.value = ''
      },
    })
  }
  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <Link href={pathFor('/settings/carriers')} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="text-sm font-semibold">{carrier.name}</h1>
          {carrier.code && <span className="text-xs font-mono text-muted-foreground">{carrier.code}</span>}
        </div>
        <div className="flex items-center gap-2">
          {usePage<{ current_user: { role: string } | null }>().props.current_user?.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => router.visit(pathFor(`/settings/carriers/${carrier.id}/api_credentials`))}
            >
              <Key className="h-3.5 w-3.5 mr-1" />
              API Credentials
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => router.visit(pathFor(`/settings/carriers/${carrier.id}/edit`))}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Edit
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        {/* Contact Info */}
        <section className="space-y-1">
          {carrier.contact_email && <p className="text-[13px] text-muted-foreground">{carrier.contact_email}</p>}
          {carrier.contact_phone && <p className="text-[13px] text-muted-foreground">{carrier.contact_phone}</p>}
          {carrier.website && (
            <a href={carrier.website} target="_blank" rel="noreferrer" className="text-[13px] text-primary hover:underline">
              {carrier.website}
            </a>
          )}
        </section>

        {/* Booking History */}
        <section>
          <h2 className="text-[13px] font-semibold text-foreground mb-3">Booking History</h2>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Carrier Booking ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Client</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Route</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Sailing</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Container</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(b.show_url)}>
                    <td className="px-4 py-2 text-[13px] font-medium">{formatBookingRef(b.carrier_booking_id)}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{b.client_email || '-'}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">
                      {b.origin_port && b.destination_port ? `${b.origin_port} → ${b.destination_port}` : '-'}
                    </td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{b.sailing_date || '-'}</td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">
                      {b.container_count ? `${b.container_count}x ${shortContainerType(b.container_type)}` : '-'}
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-4 text-center text-xs text-muted-foreground">No bookings</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Rate Sheets — a carrier's contract-rate source */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-foreground">Rate Sheets</h2>
            <input ref={fileInput} type="file" accept=".xlsx" className="hidden" onChange={handleRateSheetFile} />
            <Button variant="outline" size="sm" className="text-xs" disabled={uploading} onClick={() => fileInput.current?.click()}>
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              Upload rate sheet
            </Button>
          </div>
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">File</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground w-[130px]">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground w-[70px]">Rows</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground w-[200px]">Validity</th>
                </tr>
              </thead>
              <tbody>
                {rate_sheets.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => router.visit(s.show_url)}>
                    <td className="px-4 py-2 text-[13px] font-medium">
                      {s.original_filename}
                      {s.parse_format && <div className="text-[11px] text-muted-foreground">{s.parse_format}</div>}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${rateSheetStatusClass(s.parse_status)}`}>
                        {s.parse_status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[13px] text-muted-foreground">{s.row_count}</td>
                    <td className="px-4 py-2 text-[12px] text-muted-foreground">{rateSheetValidity(s)}</td>
                  </tr>
                ))}
                {rate_sheets.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-4 text-center text-xs text-muted-foreground">No rate sheets. Upload an .xlsx to add contract rates for this carrier.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  )
}

function rateSheetStatusClass(status: string): string {
  const map: Record<string, string> = {
    parsed: 'bg-green-50 text-green-700',
    needs_review: 'bg-amber-50 text-amber-700',
    failed: 'bg-red-50 text-red-700',
    pending: 'bg-blue-50 text-blue-700',
    parsing: 'bg-blue-50 text-blue-700',
  }
  return map[status] || 'bg-muted text-muted-foreground'
}

function rateSheetValidity(s: RateSheetRow): string {
  if (s.effective_date && s.expiry_date) return `${s.effective_date} → ${s.expiry_date}`
  if (s.expiry_date) return `until ${s.expiry_date}`
  if (s.effective_date) return `from ${s.effective_date}`
  return '—'
}

CarriersShow.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>
    <SettingsLayout>{page}</SettingsLayout>
  </AuthenticatedLayout>
)
